import cx from 'classnames'
import { useState, useEffect, useMemo } from 'react'
import dayjs from 'dayjs'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { observer } from 'mobx-react-lite'
import { Spinner } from 'common/Spinner/Spinner'
import { Text } from 'blaze-ui/Text/Text'
import { useRootStore } from 'stores/RootStore'
import type { GenerationType } from 'stores/GenerationTimestampStore'
import Styles from './GeneratingOverlay.module.scss'

// Timing configs per generation type
// AI: 2 minutes total (8+8+8+8+44+44 = 120 seconds)
// Default: 1 minute total (8+12+40 = 60 seconds)
const TIMING_CONFIG: Record<
  GenerationType,
  {
    totalSeconds: number
    messageDurations: number[]
    messages: string[]
  }
> = {
  ai: {
    totalSeconds: 120,
    messageDurations: [8000, 8000, 8000, 8000, 44000, 44000],
    messages: [
      'Loading your brand profile...',
      'Exploring creative directions...',
      'Composing the layout...',
      'Applying your brand style...',
      'Generating visual elements...',
      'Refining the details...',
    ],
  },
  default: {
    totalSeconds: 60,
    messageDurations: [8000, 12000, 40000],
    messages: ['Applying changes...', 'Cooking up your design...', 'Spinning up the preview...'],
  },
}

interface GeneratingOverlayProps {
  isVisible: boolean
  designKey?: string
  message?: string
  blurBackdrop?: boolean
  centered?: boolean
}

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

const getMessageIndexForElapsed = (elapsedMs: number, messageDurations: number[]): number => {
  let accumulatedTime = 0
  for (let i = 0; i < messageDurations.length; i++) {
    accumulatedTime += messageDurations[i]
    if (elapsedMs < accumulatedTime) {
      return i
    }
  }
  return messageDurations.length - 1
}

export const GeneratingOverlay = observer(
  ({ isVisible, designKey, message, blurBackdrop, centered }: GeneratingOverlayProps) => {
  const { generationTimestampStore } = useRootStore()
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [maxSeconds, setMaxSeconds] = useState(0)

  const startedAt = designKey ? generationTimestampStore.getStartedAt(designKey) : undefined
  const generationType = designKey ? generationTimestampStore.getGenerationType(designKey) : undefined
  const storeMessage = designKey ? generationTimestampStore.getMessage(designKey) : undefined
  const replacingImageUrl = designKey ? generationTimestampStore.getReplacingImageUrl(designKey) : undefined
  const customMessage = message ?? storeMessage
  const config = useMemo(() => TIMING_CONFIG[generationType ?? 'ai'], [generationType])

  const displayText = useMemo(() => `${Math.floor(maxSeconds / 60)} m`, [maxSeconds])
  const messageIndex = useMemo(
    () => getMessageIndexForElapsed(elapsedSeconds * 1000, config.messageDurations),
    [elapsedSeconds, config.messageDurations],
  )

  useEffect(() => {
    if (!isVisible) {
      setElapsedSeconds(0)
      setMaxSeconds(config.totalSeconds)
      return
    }

    if (startedAt) {
      const initialElapsed = dayjs().diff(dayjs(startedAt), 'second')
      const initialMax = Math.max(config.totalSeconds, Math.ceil(initialElapsed / 60) * 60)
      setMaxSeconds(initialMax)
      setElapsedSeconds(initialElapsed)
    } else {
      setMaxSeconds(config.totalSeconds)
    }

    const timerInterval = setInterval(() => {
      if (startedAt) {
        const elapsed = dayjs().diff(dayjs(startedAt), 'second')
        setElapsedSeconds(elapsed)
        setMaxSeconds((prevMax) => (elapsed >= prevMax ? prevMax + 60 : prevMax))
      } else {
        setElapsedSeconds((prev) => prev + 1)
      }
    }, 1000)

    return () => clearInterval(timerInterval)
  }, [isVisible, startedAt, config])

  useEffect(() => {
    if (!isVisible || startedAt) return
    if (elapsedSeconds >= maxSeconds && maxSeconds > 0) {
      setMaxSeconds((prevMax) => prevMax + 60)
    }
  }, [isVisible, startedAt, elapsedSeconds, maxSeconds])

  if (!isVisible) return null

  return (
    <>
      {replacingImageUrl && <img src={replacingImageUrl} alt="replacement" className={Styles.replacingImage} />}
      <div
        className={blurBackdrop ? `${Styles.overlay} ${Styles.blurBackdrop}` : Styles.overlay}
        data-testid="generating-overlay"
      >
        {centered ? (
          <div className={cx(Styles.content, Styles.contentCentered)}>
            <Spinner className={Styles.spinner} variant="transparent" />
            <Text className={Styles.messageLabel} color="var(--light-90)" lineClamp={1}>
              {customMessage ?? config.messages[messageIndex]}
            </Text>
            <span className={Styles.timer}>
              <Text className={Styles.timerText} color="var(--light-90)">
                {formatTime(elapsedSeconds)}
              </Text>
            </span>
          </div>
        ) : (
          <div className={Styles.content}>
            <div className={Styles.messageRow}>
              <Spinner className={Styles.spinner} variant="transparent" />
              <div className={Styles.messageContainer}>
              {customMessage ? (
                <span className={Styles.messageText}>
                  <Text className={Styles.messageLabel} color="var(--light-90)" lineClamp={1}>
                    {customMessage}
                  </Text>
                </span>
              ) : (
                <TransitionGroup component={null}>
                  <CSSTransition
                    key={messageIndex}
                    timeout={600}
                    classNames={{
                      enter: Styles.messageEnter,
                      enterActive: Styles.messageEnterActive,
                      exit: Styles.messageExit,
                      exitActive: Styles.messageExitActive,
                    }}
                  >
                    <span className={Styles.messageText}>
                      <Text className={Styles.messageLabel} color="var(--light-90)" lineClamp={1}>
                        {config.messages[messageIndex]}
                      </Text>
                    </span>
                  </CSSTransition>
                </TransitionGroup>
              )}
            </div>
          </div>
          <span className={Styles.timer}>
            <Text className={Styles.timerText} color="var(--light-90)">
              {formatTime(elapsedSeconds)}
            </Text>
            <Text className={Styles.timerText} color="var(--light-60)">
              /{displayText}
            </Text>
          </span>
          </div>
        )}
      </div>
    </>
  )
})
